import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './App.css';
import AIAssist from './pages/AIAssist';
import FormsPanel from './pages/FormsPanel';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import BackupPanel from './BackupPanel';

const API = axios.create({ baseURL: process.env.REACT_APP_API_URL });

API.interceptors.request.use(config => {
  const token = localStorage.getItem('cms_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const FIELD_TYPES = [
  { type: 'string',  icon: 'Aa', label: 'String',  desc: 'Short text' },
  { type: 'text',    icon: '¶',  label: 'Text',    desc: 'Long paragraph' },
  { type: 'number',  icon: '##', label: 'Number',  desc: 'Integer or decimal' },
  { type: 'image',   icon: '⊡',  label: 'Image',   desc: 'Image URL' },
  { type: 'video',   icon: '▶',  label: 'Video',   desc: 'Video URL or file' },
  { type: 'file',    icon: '📎', label: 'File',    desc: 'Any file attachment' },
  { type: 'boolean', icon: '◐',  label: 'Boolean', desc: 'True / False' },
  { type: 'date',    icon: '▦',  label: 'Date',    desc: 'Date value' },
  { type: 'array',   icon: '❏',  label: 'Array',   desc: 'List of objects' },
  { type: 'html',    icon: '<>', label: 'HTML',    desc: 'Rich HTML editor' },
];

const uid = () => Math.random().toString(36).slice(2, 8);
const slugify = s => s.toLowerCase().replace(/\s+/g, '-');
const COLORS = ['#6366f1','#0ea5e9','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899'];
const typeIcon = type => FIELD_TYPES.find(f => f.type === type)?.icon || '?';

// HTML field: Visual (Quill) + Code (raw HTML). Both views share ONE draft, so
// an edit in either shows in the other the instant you switch — no save needed.
// A ref tracks what we last emitted, so the sync effect only pulls in genuine
// outside changes (loading a doc, discard) and never overwrites live typing.
function HtmlEditor({ value, onChange, style }) {
  const [mode, setMode] = useState('visual'); // 'visual' | 'code'
  const [draft, setDraft] = useState(value || '');
  const lastEmitted = React.useRef(value || '');

  React.useEffect(() => {
    const incoming = value || '';
    if (incoming !== lastEmitted.current) {   // real external change only
      setDraft(incoming);
      lastEmitted.current = incoming;
    }
  }, [value]);

  const update = (html) => {
    lastEmitted.current = html;   // mark as our own edit so the effect ignores the echo
    setDraft(html);               // reflect immediately in BOTH views
    onChange(html);               // push up to parent (autosave etc.)
  };

  const tabBtn = (active) => ({
    fontSize: 12,
    fontFamily: 'var(--mono), monospace',
    padding: '6px 16px',
    borderRadius: 6,
    border: '1px solid ' + (active ? 'var(--accent, #6366f1)' : 'var(--border2, #cbd5e1)'),
    background: active ? 'var(--accent, #6366f1)' : 'transparent',
    color: active ? '#fff' : 'var(--muted2, #475569)',
    cursor: 'pointer',
    fontWeight: active ? 600 : 400,
  });

  return (
    <div style={{ border: '1px solid var(--border2, #cbd5e1)', borderRadius: 8, overflow: 'hidden', background: '#fff', ...style }}>
      <div style={{ display: 'flex', gap: 6, padding: 8, background: 'var(--bg, #f8fafc)', borderBottom: '1px solid var(--border2, #cbd5e1)' }}>
        <button type="button" style={tabBtn(mode === 'visual')} onClick={() => setMode('visual')}>Visual</button>
        <button type="button" style={tabBtn(mode === 'code')} onClick={() => setMode('code')}>{'<> Code'}</button>
      </div>
      {mode === 'code' ? (
        <textarea
          value={draft}
          onChange={e => update(e.target.value)}
          spellCheck={false}
          placeholder="<p>Write or paste HTML here…</p>"
          style={{
            width: '100%', minHeight: 340, boxSizing: 'border-box',
            border: 'none', outline: 'none', padding: 16,
            fontFamily: 'var(--mono), monospace', fontSize: 13, lineHeight: 1.6,
            color: '#1e293b', background: '#fff', whiteSpace: 'pre-wrap',
            tabSize: 2, resize: 'vertical',
          }}
        />
      ) : (
        <ReactQuill value={draft} onChange={update} theme="snow" />
      )}
    </div>
  );
}

// Builds the public URL for a published blog post
const getBlogPostUrl = (doc) => {
  if (!doc) return '';
  const slug = doc.tree?.data?.slug || slugify(doc.title || '');
  return `https://sunkind.in/blogs/${slug}`;
};

// Pre-built field set for a blog post — used by the "New Blog Post" button.
// Edit this list to change what every new blog post starts with.
const BLOG_TEMPLATE = [
  { name: 'title',         label: 'Title',          type: 'string' },
  { name: 'slug',          label: 'Slug',           type: 'string' },
  { name: 'excerpt',       label: 'Excerpt',        type: 'text'   },
  { name: 'content',       label: 'Content',        type: 'html'   },
  { name: 'featuredImage', label: 'Featured Image', type: 'image'  },
  { name: 'category',      label: 'Category',       type: 'string' },
  { name: 'author',        label: 'Author',         type: 'string' },
  { name: 'publishedAt',   label: 'Date',           type: 'date'   },
  // SEO — what Google and browser tabs show. Leave blank to fall back to
  // the post Title / Excerpt / actual page URL automatically.
  { name: 'metaTitle',         label: 'SEO: Meta Title',            type: 'string' },
  { name: 'metaDescription',   label: 'SEO: Meta Description',      type: 'text'   },
  { name: 'metaKeywords',      label: 'SEO: Meta Keywords',         type: 'string' },
  { name: 'canonicalUrl',      label: 'SEO: Canonical URL',         type: 'string' },
  { name: 'noindex',           label: 'SEO: Noindex (hide from search engines)',        type: 'boolean' },
  { name: 'nofollow',          label: 'SEO: Nofollow (don\'t follow links on this page)', type: 'boolean' },
  // Open Graph — used for link previews everywhere (Facebook, LinkedIn,
  // Twitter/X, WhatsApp, etc. all read the same og: tags). Leave blank to
  // fall back to the Meta Title/Description/Featured Image above.
  { name: 'ogTitle',           label: 'OG Title',       type: 'string' },
  { name: 'ogDescription',     label: 'OG Description', type: 'text'   },
  { name: 'ogImage',           label: 'OG Image',       type: 'image'  },
  // 301 Redirects — if this post's slug ever changes, add its PREVIOUS
  // slug(s) here. Anyone visiting an old slug gets sent to the current
  // one automatically with a real 301 (permanent redirect), so old
  // links/bookmarks/search rankings keep working instead of 404ing.
  { name: 'oldSlugs', label: '301 Redirects: Old URLs (previous slugs)', type: 'array', subFields: [
      { id: 'oldslug1', name: 'slug', label: 'Old Slug', type: 'string' },
  ]},
];

function getNodeByPath(tree, path) {
  let node = tree;
  for (const id of path) {
    node = (node.children || []).find(c => c.id === id);
    if (!node) return null;
  }
  return node;
}

function updateNodeByPath(tree, path, updater) {
  if (path.length === 0) return updater(tree);
  return {
    ...tree,
    children: (tree.children || []).map(child =>
      child.id === path[0]
        ? updateNodeByPath(child, path.slice(1), updater)
        : child
    )
  };
}

function previewValue(field, value) {
  if (value === undefined || value === null || value === '') return <span className="preview-empty">Empty</span>;
  if (field.type === 'boolean') return <span className="preview-val">{value ? 'True' : 'False'}</span>;
  if (field.type === 'html') return <span className="preview-val" style={{fontSize:11,color:'var(--accent)'}}>HTML content</span>;
  if (field.type === 'video') return (
    <span className="preview-val">
      {value ? <span style={{fontSize:11,color:'var(--accent)'}}>▶ Video set</span> : <span className="preview-empty">Empty</span>}
    </span>
  );
  if (field.type === 'file') return (
    <span className="preview-val">
      {value ? <span style={{fontSize:11,color:'var(--accent)'}}>📎 {value.split('/').pop()}</span> : <span className="preview-empty">Empty</span>}
    </span>
  );
  if (field.type === 'image') return (
    <span className="preview-val">
      <img src={value} alt="" style={{width:20,height:20,objectFit:'cover',borderRadius:3}} onError={e=>e.target.style.display='none'} />
      <span style={{opacity:.5,fontSize:11,maxWidth:120,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{value}</span>
    </span>
  );
  if (field.type === 'array') {
    const arr = Array.isArray(value) ? value : [];
    return <span className="preview-val">{arr.length} item{arr.length !== 1 ? 's' : ''}</span>;
  }
  return <span className="preview-val" style={{maxWidth:180,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{String(value)}</span>;
}

function Modal({ onClose, children, width = 420 }) {
  return (
    <div className="overlay">
      <div className="modal" style={{ width }} onClick={e => e.stopPropagation()}>{children}</div>
    </div>
  );
}

function NameModal({ title, placeholder, onConfirm, onClose, showKey = false }) {
  const [name, setName] = useState('');
  const [key, setKey] = useState('');
  const [keyError, setKeyError] = useState('');
  const autoKey = name.trim() ? name.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '') : '';
  const finalKey = key.trim() || autoKey;

  const handleNameChange = (val) => {
    setName(val);
    setKeyError('');
  };

  const handleConfirm = () => {
    if (!name.trim()) return;
    setKeyError('');
    onConfirm(name.trim(), finalKey, (err) => setKeyError(err));
  };

  return (
    <Modal onClose={onClose}>
      <div className="modal-title">{title}</div>
      <label className="mlabel">Name</label>
      <input className="minput" value={name} onChange={e => handleNameChange(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && handleConfirm()}
        placeholder={placeholder} autoFocus />
      {showKey && name.trim() && (
        <>
          <label className="mlabel" style={{marginTop:14}}>Key <span style={{color:'var(--muted)',fontWeight:400,textTransform:'none',letterSpacing:0}}>(auto-generated, you can change it)</span></label>
          <input className="minput" style={{fontFamily:'var(--mono)',fontSize:12,borderColor: keyError ? 'var(--red)' : ''}}
            value={key || autoKey}
            onChange={e => { setKey(e.target.value); setKeyError(''); }}
            placeholder={autoKey} />
          {keyError
            ? <div style={{fontSize:11,color:'var(--red)',marginTop:4}}>⚠ {keyError}</div>
            : <div style={{fontSize:11,color:'var(--muted)',marginTop:4}}>This _key identifies this section in the API.</div>
          }
        </>
      )}
      <div className="mactions">
        <button className="btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn-solid" onClick={handleConfirm}>Create</button>
      </div>
    </Modal>
  );
}

function EditFieldLabelModal({ field, onConfirm, onClose }) {
  const [label, setLabel] = useState(field?.label || '');

  const handleConfirm = () => {
    if (!label.trim()) return;
    onConfirm(label.trim());
  };

  return (
    <Modal onClose={onClose}>
      <div className="modal-title">Edit Field Label</div>
      <label className="mlabel">Display Name</label>
      <input className="minput" value={label} onChange={e => setLabel(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && handleConfirm()}
        placeholder="Enter field display name" autoFocus />
      <div style={{fontSize:11,color:'var(--muted)',marginTop:8}}>
        Field key "{field?.name}" will remain unchanged
      </div>
      <div className="mactions">
        <button className="btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn-solid" onClick={handleConfirm}>Update</button>
      </div>
    </Modal>
  );
}

function AddFieldModal({ onConfirm, onClose }) {
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [type, setType] = useState('string');
  const [subFields, setSubFields] = useState([]);
  const [sfName, setSfName] = useState('');
  const [sfType, setSfType] = useState('string');

  const handleNext = () => {
    if (!name.trim()) return;
    if (type === 'array') { setStep(2); return; }
    onConfirm({ id: uid(), name: name.trim(), label: name.trim(), type });
  };
  const addSubField = () => {
    if (!sfName.trim()) return;
    setSubFields(p => [...p, { id: uid(), name: sfName.trim(), label: sfName.trim(), type: sfType }]);
    setSfName(''); setSfType('string');
  };

  return (
    <Modal onClose={onClose} width={480}>
      {step === 1 && <>
        <div className="modal-title">Add Data Field</div>
        <label className="mlabel">Field Name</label>
        <input className="minput" value={name} onChange={e => setName(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleNext()}
          placeholder="e.g. title, description, image..." autoFocus />
        <label className="mlabel" style={{marginTop:16}}>Field Type</label>
        <div className="ftype-grid">
          {FIELD_TYPES.map(f => (
            <div key={f.type} className={`ftype-card ${type === f.type ? 'sel' : ''}`} onClick={() => setType(f.type)}>
              <div className="ftype-icon">{f.icon}</div>
              <div className="ftype-label">{f.label}</div>
              <div className="ftype-desc">{f.desc}</div>
            </div>
          ))}
        </div>
        <div className="mactions">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-solid" onClick={handleNext}>{type === 'array' ? 'Next →' : 'Add Field'}</button>
        </div>
      </>}
      {step === 2 && <>
        <div className="modal-title">Array Structure — <span className="accent">{name}</span></div>
        <p className="modal-sub">Each item in this array will contain:</p>
        <div className="sf-builder">
          <div className="sf-row">
            <input className="minput" style={{flex:1}} value={sfName} onChange={e => setSfName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addSubField()} placeholder="Sub-field name..." />
            <select className="mselect" value={sfType} onChange={e => setSfType(e.target.value)}>
              {FIELD_TYPES.filter(f => f.type !== 'array').map(f => <option key={f.type} value={f.type}>{f.label}</option>)}
            </select>
            <button className="btn-solid" style={{padding:'9px 16px'}} onClick={addSubField}>+</button>
          </div>
          {subFields.length > 0 && (
            <div className="sf-list">
              {subFields.map((sf, i) => (
                <div key={sf.id} className="sf-item">
                  <span className="sf-name">{sf.name}</span>
                  <span className="badge">{sf.type}</span>
                  <button className="rm-btn" onClick={() => setSubFields(p => p.filter((_, j) => j !== i))}>×</button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="mactions">
          <button className="btn-ghost" onClick={() => setStep(1)}>← Back</button>
          <button className="btn-solid" onClick={() => subFields.length > 0 && onConfirm({ id: uid(), name: name.trim(), label: name.trim(), type: 'array', subFields })} disabled={subFields.length === 0}>
            Add Array Field
          </button>
        </div>
      </>}
    </Modal>
  );
}

function AddArrayItemModal({ field, onConfirm, onClose }) {
  const [vals, setVals] = useState({});
  const set = (k, v) => setVals(p => ({ ...p, [k]: v }));
  return (
    <Modal onClose={onClose} width={460}>
      <div className="modal-title">Add Item — <span className="accent">{field.label}</span></div>
      {(field.subFields || []).map(sf => (
        <div key={sf.id} style={{marginBottom:14}}>
          <label className="mlabel">{sf.label} <span className="badge">{sf.type}</span></label>
          {sf.type === 'text' ? <textarea className="minput" rows={3} value={vals[sf.name]||''} onChange={e => set(sf.name, e.target.value)} placeholder={`Enter ${sf.label}...`} />
          : sf.type === 'html' ? (
            <HtmlEditor value={vals[sf.name]||''} onChange={v => set(sf.name, v)} style={{background:'white',borderRadius:6}} />
          ) : sf.type === 'boolean' ? (
            <div className="bool-toggle">
              <button className={`bool-btn ${vals[sf.name] ? 'on' : ''}`} onClick={() => set(sf.name, true)}>True</button>
              <button className={`bool-btn ${!vals[sf.name] ? 'on' : ''}`} onClick={() => set(sf.name, false)}>False</button>
            </div>
          ) : sf.type === 'video' ? (
            <div>
              <input className="minput" value={vals[sf.name]||''} onChange={e => set(sf.name, e.target.value)} placeholder="Paste video URL or upload..." style={{marginBottom:6}} />
              <div style={{display:'flex',alignItems:'center',gap:8,padding:'8px 12px',border:'1px dashed var(--border2)',borderRadius:'var(--r-sm)',background:'var(--bg)',cursor:'pointer'}}
                onClick={() => document.getElementById('vid-'+sf.name)?.click()}>
                <span style={{fontSize:12,color:'var(--muted2)'}}>&#8679; Upload video</span>
                <input id={'vid-'+sf.name} type="file" accept="video/*" style={{display:'none'}}
                  onChange={async e => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const fd = new FormData();
                    fd.append('image', file);
                    try {
                      const res = await fetch(process.env.REACT_APP_API_URL+'api/upload', {method:'POST', body:fd});
                      const data = await res.json();
                      if (data.url) set(sf.name, data.url);
                    } catch(err) { alert('Upload failed'); }
                  }} />
              </div>
              {vals[sf.name] && <video src={vals[sf.name]} controls style={{width:'100%',maxHeight:100,borderRadius:6,marginTop:6}} />}
            </div>
          ) : sf.type === 'image' ? (
            <div>
              <div style={{display:'flex',alignItems:'center',gap:8,padding:'8px 12px',border:'1px dashed var(--border2)',borderRadius:'var(--r-sm)',background:'var(--bg)',cursor:'pointer'}}
                onClick={() => document.getElementById('up-'+sf.name)?.click()}>
                <span style={{fontSize:12,color:'var(--muted2)'}}>&#8679; {vals[sf.name] ? 'Replace image' : 'Upload image'}</span>
                <input id={'up-'+sf.name} type="file" accept="image/*" style={{display:'none'}}
                  onChange={async e => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const fd = new FormData();
                    fd.append('image', file);
                    try {
                      const res = await fetch(process.env.REACT_APP_API_URL+'api/upload', {method:'POST', body:fd});
                      const data = await res.json();
                      if (data.url) set(sf.name, data.url);
                    } catch(err) { alert('Upload failed'); }
                  }} />
              </div>
              {vals[sf.name] && <img src={vals[sf.name]} alt="" style={{width:200,height:130,objectFit:'cover',borderRadius:6,marginTop:6}} onError={e => e.target.style.display='none'} />}
              {vals[sf.name] && (
                <details className="image-seo-details" style={{marginTop:6}}>
                  <summary style={{fontSize:11,color:'var(--muted2)',cursor:'pointer',userSelect:'none'}}>Alt text, caption, description, credit ▾</summary>
                  <div style={{marginTop:8,display:'flex',flexDirection:'column',gap:8}}>
                    <div>
                      <label className="mlabel" style={{fontSize:10}}>Alt Text</label>
                      <input className="minput" style={{fontSize:12}} value={vals[sf.name + '_alt']||''} onChange={e => set(sf.name + '_alt', e.target.value)} placeholder="Describe this image..." />
                    </div>
                    <div>
                      <label className="mlabel" style={{fontSize:10}}>Caption</label>
                      <input className="minput" style={{fontSize:12}} value={vals[sf.name + '_caption']||''} onChange={e => set(sf.name + '_caption', e.target.value)} placeholder="Text shown under the image..." />
                    </div>
                    <div>
                      <label className="mlabel" style={{fontSize:10}}>Description</label>
                      <textarea className="minput" rows={2} style={{fontSize:12}} value={vals[sf.name + '_description']||''} onChange={e => set(sf.name + '_description', e.target.value)} placeholder="Longer internal/SEO description..." />
                    </div>
                    <div>
                      <label className="mlabel" style={{fontSize:10}}>Credit / Attribution</label>
                      <input className="minput" style={{fontSize:12}} value={vals[sf.name + '_credit']||''} onChange={e => set(sf.name + '_credit', e.target.value)} placeholder="Photographer or source credit..." />
                    </div>
                  </div>
                </details>
              )}
            </div>
          ) : sf.type === 'file' ? (
            <div>
              <div style={{display:'flex',alignItems:'center',gap:8,padding:'8px 12px',border:'1px dashed var(--border2)',borderRadius:'var(--r-sm)',background:'var(--bg)',cursor:'pointer'}}
                onClick={() => document.getElementById('file-'+sf.name)?.click()}>
                <span style={{fontSize:12,color:'var(--muted2)'}}>📎 {vals[sf.name] ? 'Replace file' : 'Upload file'}</span>
                <input id={'file-'+sf.name} type="file" style={{display:'none'}}
                  onChange={async e => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const fd = new FormData();
                    fd.append('image', file);
                    try {
                      const res = await fetch(process.env.REACT_APP_API_URL+'api/upload', {method:'POST', body:fd});
                      const data = await res.json();
                      if (data.url) set(sf.name, data.url);
                    } catch(err) { alert('Upload failed'); }
                  }} />
              </div>
              {vals[sf.name] && <div style={{fontSize:11,color:'var(--muted)',marginTop:6,padding:'6px 8px',background:'var(--bg)',borderRadius:4,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>✓ {vals[sf.name].split('/').pop()}</div>}
            </div>
          ) : <input className="minput" type={sf.type==='number'?'number':sf.type==='date'?'date':'text'}
              value={vals[sf.name]||''} onChange={e => set(sf.name, sf.type==='number' ? parseFloat(e.target.value)||0 : e.target.value)}
              placeholder={`Enter ${sf.label}...`} />}
        </div>
      ))}
      <div className="mactions">
        <button className="btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn-solid" onClick={() => onConfirm({ ...vals })}>Add Item</button>
      </div>
    </Modal>
  );
}

function EditFieldModal({ field, value, onSave, onAutoSave, onClose }) {
  const [val, setVal] = useState(value !== undefined ? value : field.type==='array'?[]:field.type==='boolean'?false:field.type==='number'?0:'');
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [editingItemIndex, setEditingItemIndex] = useState(null);
  const [editingItemVals, setEditingItemVals] = useState({});
  const [autoSaved, setAutoSaved] = useState(false);
  const autoSaveTimer = React.useRef(null);

  React.useEffect(() => {
    clearTimeout(autoSaveTimer.current);
    if (!onAutoSave) return;
    autoSaveTimer.current = setTimeout(() => {
      onAutoSave(val);
      setAutoSaved(true);
      setTimeout(() => setAutoSaved(false), 2000);
    }, 1500);
    return () => clearTimeout(autoSaveTimer.current);
  }, [val, onAutoSave]);
  return (
    <Modal onClose={onClose} width={500}>
      <div className="modal-title">
        <span style={{fontFamily:'var(--mono)',fontSize:14,color:'var(--muted2)'}}>{typeIcon(field.type)}</span>
        {field.label}
        <span className="badge" style={{marginLeft:4}}>{field.type}</span>
      </div>
      {field.type === 'string' && <input className="minput" value={val} onChange={e => setVal(e.target.value)} autoFocus />}
      {field.type === 'text' && <textarea className="minput" rows={7} value={val} onChange={e => setVal(e.target.value)} autoFocus />}
      {field.type === 'html' && (
        <HtmlEditor value={val} onChange={setVal} style={{background:'white',borderRadius:6,marginTop:8}} />
      )}
      {field.type === 'number' && <input className="minput" type="number" value={val} onChange={e => setVal(parseFloat(e.target.value)||0)} autoFocus />}
      {field.type === 'date' && <input className="minput" type="date" value={val} onChange={e => setVal(e.target.value)} autoFocus />}
      {field.type === 'boolean' && (
        <div className="bool-toggle">
          <button className={`bool-btn ${val ? 'on' : ''}`} onClick={() => setVal(true)}>True</button>
          <button className={`bool-btn ${!val ? 'on' : ''}`} onClick={() => setVal(false)}>False</button>
        </div>
      )}
      {field.type === 'video' && (
        <div>
          <input className="minput" value={val} onChange={e => setVal(e.target.value)} placeholder="Paste video URL or upload..." autoFocus style={{flex:1,marginBottom:8}} />
          <div style={{display:'flex',alignItems:'center',gap:10,padding:'10px 12px',border:'1px dashed var(--border2)',borderRadius:'var(--r-sm)',background:'var(--bg)',cursor:'pointer'}}
            onClick={() => document.getElementById('vid-upload-'+field.name)?.click()}>
            <span style={{fontSize:13,color:'var(--muted2)'}}>▶ Upload video</span>
            <span style={{fontSize:11,color:'var(--muted)'}}>MP4, WebM, MOV up to 50MB</span>
            <input id={'vid-upload-'+field.name} type="file" accept="video/*" style={{display:'none'}}
              onChange={async e => {
                const file = e.target.files?.[0];
                if (!file) return;
                const formData = new FormData();
                formData.append('image', file);
                try {
                  const res = await fetch(process.env.REACT_APP_API_URL+'api/upload', { method:'POST', body: formData });
                  const data = await res.json();
                  if (data.url) setVal(data.url);
                } catch(err) { alert('Upload failed'); }
              }} />
          </div>
          {val && <video src={val} controls style={{width:220,height:140,objectFit:'cover',borderRadius:8,marginTop:10,display:'block'}} />}
        </div>
      )}
      {field.type === 'image' && (
        <div>
          <div style={{display:'flex',alignItems:'center',gap:10,padding:'10px 12px',border:'1px dashed var(--border2)',borderRadius:'var(--r-sm)',background:'var(--bg)',cursor:'pointer'}}
            onClick={() => document.getElementById('img-upload-'+field.name)?.click()}>
            <span style={{fontSize:13,color:'var(--muted2)'}}>⬆ {val ? 'Replace image' : 'Upload image'}</span>
            <span style={{fontSize:11,color:'var(--muted)'}}>JPG, PNG, GIF, WebP</span>
            <input id={'img-upload-'+field.name} type="file" accept="image/*" style={{display:'none'}}
              onChange={async e => {
                const file = e.target.files?.[0];
                if (!file) return;
                const formData = new FormData();
                formData.append('image', file);
                try {
                  const res = await fetch(process.env.REACT_APP_API_URL+'api/upload', { method:'POST', body: formData });
                  const data = await res.json();
                  if (data.url) setVal(data.url);
                } catch(err) { alert('Upload failed'); }
              }} />
          </div>
          {val && <img src={val} alt="" style={{width:220,height:150,objectFit:'cover',borderRadius:8,marginTop:10,display:'block'}} onError={e => e.target.style.display='none'} />}
        </div>
      )}
      {field.type === 'file' && (
        <div>
          <div style={{display:'flex',alignItems:'center',gap:10,padding:'10px 12px',border:'1px dashed var(--border2)',borderRadius:'var(--r-sm)',background:'var(--bg)',cursor:'pointer'}}
            onClick={() => document.getElementById('file-upload-'+field.name)?.click()}>
            <span style={{fontSize:13,color:'var(--muted2)'}}>📎 {val ? 'Replace file' : 'Upload file'}</span>
            <span style={{fontSize:11,color:'var(--muted)'}}>Any file type</span>
            <input id={'file-upload-'+field.name} type="file" style={{display:'none'}}
              onChange={async e => {
                const file = e.target.files?.[0];
                if (!file) return;
                const formData = new FormData();
                formData.append('image', file);
                try {
                  const res = await fetch(process.env.REACT_APP_API_URL+'api/upload', { method:'POST', body: formData });
                  const data = await res.json();
                  if (data.url) setVal(data.url);
                } catch(err) { alert('Upload failed'); }
              }} />
          </div>
          {val && <div style={{fontSize:12,color:'var(--muted)',marginTop:10,padding:'8px 10px',background:'var(--bg)',borderRadius:6,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',border:'1px solid var(--border2)'}}>✓ {val.split('/').pop()}</div>}
        </div>
      )}
      {field.type === 'array' && (
        <div className="array-edit">
          <div className="array-items-list">
            {(Array.isArray(val) ? val : []).map((item, i) => (
              <div key={i} className="array-item-row">
                {editingItemIndex === i ? (
                  <div style={{flex:1,display:'flex',flexDirection:'column',gap:8,padding:'8px 0'}}>
                    {(field.subFields||[]).map(sf => (
                      <div key={sf.id}>
                        <label className="mlabel">{sf.label}</label>
                        {sf.type==='text' ? <textarea className="minput" rows={2} value={editingItemVals[sf.name]||''} onChange={e=>setEditingItemVals(p=>({...p,[sf.name]:e.target.value}))} />
                        : sf.type==='html' ? (
                          <HtmlEditor value={editingItemVals[sf.name]||''} onChange={v=>setEditingItemVals(p=>({...p,[sf.name]:v}))} style={{background:'white',borderRadius:6}} />
                        ) : sf.type==='boolean' ? (
                          <select className="minput" value={editingItemVals[sf.name]||'false'} onChange={e=>setEditingItemVals(p=>({...p,[sf.name]:e.target.value==='true'}))}>
                            <option value="true">True</option><option value="false">False</option>
                          </select>
                        ) : sf.type==='video' ? (
                          <div>
                            <input className="minput" value={editingItemVals[sf.name]||''} onChange={e => setEditingItemVals(p=>({...p,[sf.name]:e.target.value}))} placeholder="Paste video URL or upload..." style={{marginBottom:6}} />
                            <div style={{display:'flex',alignItems:'center',gap:8,padding:'8px 12px',border:'1px dashed var(--border2)',borderRadius:'var(--r-sm)',background:'var(--bg)',cursor:'pointer'}}
                              onClick={() => document.getElementById('edit-vid-'+sf.name)?.click()}>
                              <span style={{fontSize:12,color:'var(--muted2)'}}>&#8679; {editingItemVals[sf.name] ? 'Replace video' : 'Upload video'}</span>
                              <input id={'edit-vid-'+sf.name} type="file" accept="video/*" style={{display:'none'}}
                                onChange={async e => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;
                                  const fd = new FormData();
                                  fd.append('image', file);
                                  try {
                                    const res = await fetch(process.env.REACT_APP_API_URL+'api/upload', {method:'POST', body:fd});
                                    const data = await res.json();
                                    if (data.url) setEditingItemVals(p=>({...p,[sf.name]:data.url}));
                                  } catch(err) { alert('Upload failed'); }
                                }} />
                            </div>
                            {editingItemVals[sf.name] && <video src={editingItemVals[sf.name]} controls style={{width:'100%',maxHeight:100,borderRadius:6,marginTop:6}} />}
                          </div>
                        ) : sf.type==='image' ? (
                          <div>
                            <div style={{display:'flex',alignItems:'center',gap:8,padding:'8px 12px',border:'1px dashed var(--border2)',borderRadius:'var(--r-sm)',background:'var(--bg)',cursor:'pointer'}}
                              onClick={() => document.getElementById('edit-img-'+sf.name)?.click()}>
                              <span style={{fontSize:12,color:'var(--muted2)'}}>&#8679; {editingItemVals[sf.name] ? 'Replace image' : 'Upload image'}</span>
                              <input id={'edit-img-'+sf.name} type="file" accept="image/*" style={{display:'none'}}
                                onChange={async e => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;
                                  const fd = new FormData();
                                  fd.append('image', file);
                                  try {
                                    const res = await fetch(process.env.REACT_APP_API_URL+'api/upload', {method:'POST', body:fd});
                                    const data = await res.json();
                                    if (data.url) setEditingItemVals(p=>({...p,[sf.name]:data.url}));
                                  } catch(err) { alert('Upload failed'); }
                                }} />
                            </div>
                            {editingItemVals[sf.name] && <img src={editingItemVals[sf.name]} alt="" style={{width:200,height:130,objectFit:'cover',borderRadius:6,marginTop:6}} onError={e=>e.target.style.display='none'} />}
                            {editingItemVals[sf.name] && (
                              <details className="image-seo-details" style={{marginTop:6}}>
                                <summary style={{fontSize:11,color:'var(--muted2)',cursor:'pointer',userSelect:'none'}}>Alt text, caption, description, credit ▾</summary>
                                <div style={{marginTop:8,display:'flex',flexDirection:'column',gap:8}}>
                                  <div>
                                    <label className="mlabel" style={{fontSize:10}}>Alt Text</label>
                                    <input className="minput" style={{fontSize:12}} value={editingItemVals[sf.name + '_alt']||''} onChange={e => setEditingItemVals(p=>({...p,[sf.name + '_alt']:e.target.value}))} placeholder="Describe this image..." />
                                  </div>
                                  <div>
                                    <label className="mlabel" style={{fontSize:10}}>Caption</label>
                                    <input className="minput" style={{fontSize:12}} value={editingItemVals[sf.name + '_caption']||''} onChange={e => setEditingItemVals(p=>({...p,[sf.name + '_caption']:e.target.value}))} placeholder="Text shown under the image..." />
                                  </div>
                                  <div>
                                    <label className="mlabel" style={{fontSize:10}}>Description</label>
                                    <textarea className="minput" rows={2} style={{fontSize:12}} value={editingItemVals[sf.name + '_description']||''} onChange={e => setEditingItemVals(p=>({...p,[sf.name + '_description']:e.target.value}))} placeholder="Longer internal/SEO description..." />
                                  </div>
                                  <div>
                                    <label className="mlabel" style={{fontSize:10}}>Credit / Attribution</label>
                                    <input className="minput" style={{fontSize:12}} value={editingItemVals[sf.name + '_credit']||''} onChange={e => setEditingItemVals(p=>({...p,[sf.name + '_credit']:e.target.value}))} placeholder="Photographer or source credit..." />
                                  </div>
                                </div>
                              </details>
                            )}
                          </div>
                        ) : sf.type==='file' ? (
                          <div>
                            <div style={{display:'flex',alignItems:'center',gap:8,padding:'8px 12px',border:'1px dashed var(--border2)',borderRadius:'var(--r-sm)',background:'var(--bg)',cursor:'pointer'}}
                              onClick={() => document.getElementById('edit-file-'+sf.name)?.click()}>
                              <span style={{fontSize:12,color:'var(--muted2)'}}>📎 {editingItemVals[sf.name] ? 'Replace file' : 'Upload file'}</span>
                              <input id={'edit-file-'+sf.name} type="file" style={{display:'none'}}
                                onChange={async e => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;
                                  const fd = new FormData();
                                  fd.append('image', file);
                                  try {
                                    const res = await fetch(process.env.REACT_APP_API_URL+'api/upload', {method:'POST', body:fd});
                                    const data = await res.json();
                                    if (data.url) setEditingItemVals(p=>({...p,[sf.name]:data.url}));
                                  } catch(err) { alert('Upload failed'); }
                                }} />
                            </div>
                            {editingItemVals[sf.name] && <div style={{fontSize:11,color:'var(--muted)',marginTop:6,padding:'6px 8px',background:'var(--bg)',borderRadius:4,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>✓ {editingItemVals[sf.name].split('/').pop()}</div>}
                          </div>
                        ) : <input className="minput" type={sf.type==='number'?'number':sf.type==='date'?'date':'text'} value={editingItemVals[sf.name]||''} onChange={e=>setEditingItemVals(p=>({...p,[sf.name]:sf.type==='number'?parseFloat(e.target.value)||0:e.target.value}))} placeholder={sf.label} />}
                      </div>
                    ))}
                    <div style={{display:'flex',gap:8,marginTop:4}}>
                      <button className="btn-solid" style={{fontSize:11,padding:'5px 12px'}} onClick={() => {
                        setVal(p => p.map((it,j) => j===i ? {...it,...editingItemVals} : it));
                        setEditingItemIndex(null);
                        setEditingItemVals({});
                      }}>Save Item</button>
                      <button className="btn-ghost" style={{fontSize:11,padding:'5px 12px'}} onClick={() => { setEditingItemIndex(null); setEditingItemVals({}); }}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div className="array-item-preview">
                    {typeof item === 'object'
                      ? Object.entries(item).filter(([k]) => k !== '_key').map(([k, v]) => (
                          <span key={k} className="array-kv">
                            <span className="array-k">{k}:</span>
                            {typeof v === 'string' && (v.startsWith('http')||v.includes('.jpg')||v.includes('.png')||v.includes('.webp')||v.includes('/ipfs/'))
                              ? <img src={v} alt="" style={{width:100,height:100,objectFit:'cover',borderRadius:6,border:'1px solid var(--border2)'}} onError={e=>e.target.style.display='none'} />
                              : <span className="array-v">{String(v).slice(0,30)}{String(v).length>30?'...':''}</span>}
                          </span>))
                      : <span className="array-v">{String(item)}</span>}
                  </div>
                )}
                {editingItemIndex !== i && (
                  <div style={{display:'flex',gap:4,flexShrink:0}}>
                    <button className="edit-item-btn" onClick={() => { setEditingItemIndex(i); setEditingItemVals({...item}); }} title="Edit">✎</button>
                    <button className="rm-btn" onClick={() => setVal(p => p.filter((_, j) => j !== i))}>×</button>
                  </div>
                )}
              </div>
            ))}
            {(!Array.isArray(val) || val.length === 0) && <div className="array-empty">No items yet.</div>}
          </div>
          <button className="add-item-btn" onClick={() => setAddItemOpen(true)}>+ Add {field.label} Item</button>
          {addItemOpen && (
            <AddArrayItemModal field={field}
              onConfirm={item => { setVal(p => [...(Array.isArray(p)?p:[]), item]); setAddItemOpen(false); }}
              onClose={() => setAddItemOpen(false)} />
          )}
        </div>
      )}
      <div className="mactions">
        {autoSaved && <span style={{fontSize:12,color:'var(--accent)',marginRight:'auto'}}>✓ Auto-saved</span>}
        <button className="btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn-solid" onClick={() => { onSave(val); onClose(); }}>Done</button>
      </div>
    </Modal>
  );
}

function EditSingleItemModal({ field, item, onSave, onClose }) {
  const [vals, setVals] = React.useState({ ...item });
  const set = (k, v) => setVals(p => ({ ...p, [k]: v }));

  return (
    <div className="overlay">
      <div className="modal" style={{width:480,maxHeight:'80vh',overflowY:'auto'}} onClick={e => e.stopPropagation()}>
        <div className="modal-title">Edit — <span className="accent">{field.label}</span></div>
        {(field.subFields || []).map(sf => (
          <div key={sf.id} style={{marginBottom:14}}>
            <label className="mlabel">{sf.label} <span style={{color:'var(--muted)',fontWeight:400,textTransform:'none'}}>{sf.type}</span></label>
            {sf.type === 'text' ? (
              <textarea className="minput" rows={3} value={vals[sf.name]||''} onChange={e => set(sf.name, e.target.value)} />
            ) : sf.type === 'html' ? (
              <HtmlEditor value={vals[sf.name]||''} onChange={v => set(sf.name, v)} style={{background:'white',borderRadius:6}} />
            ) : sf.type === 'boolean' ? (
              <select className="minput" value={vals[sf.name]||'false'} onChange={e => set(sf.name, e.target.value === 'true')}>
                <option value="true">True</option><option value="false">False</option>
              </select>
            ) : sf.type === 'video' ? (
              <div>
                <input className="minput" value={vals[sf.name]||''} onChange={e => set(sf.name, e.target.value)} placeholder="Paste video URL or upload..." style={{marginBottom:6}} />
                <div style={{display:'flex',alignItems:'center',gap:8,padding:'8px 12px',border:'1px dashed var(--border2)',borderRadius:'var(--r-sm)',background:'var(--bg)',cursor:'pointer'}}
                  onClick={() => document.getElementById('si-vid-'+sf.name)?.click()}>
                  <span style={{fontSize:12,color:'var(--muted2)'}}>&#8679; {vals[sf.name] ? 'Replace video' : 'Upload video'}</span>
                  <input id={'si-vid-'+sf.name} type="file" accept="video/*" style={{display:'none'}}
                    onChange={async e => {
                      const file = e.target.files?.[0]; if (!file) return;
                      const fd = new FormData(); fd.append('image', file);
                      try {
                        const res = await fetch(process.env.REACT_APP_API_URL+'api/upload', {method:'POST',body:fd});
                        const data = await res.json();
                        if (data.url) set(sf.name, data.url);
                      } catch(err) { alert('Upload failed'); }
                    }} />
                </div>
                {vals[sf.name] && <video src={vals[sf.name]} controls style={{width:'100%',maxHeight:150,borderRadius:6,marginTop:6}} />}
              </div>
            ) : sf.type === 'image' ? (
              <div>
                <div style={{display:'flex',alignItems:'center',gap:8,padding:'8px 12px',border:'1px dashed var(--border2)',borderRadius:'var(--r-sm)',background:'var(--bg)',cursor:'pointer'}}
                  onClick={() => document.getElementById('si-img-'+sf.name)?.click()}>
                  <span style={{fontSize:12,color:'var(--muted2)'}}>&#8679; {vals[sf.name] ? 'Replace image' : 'Upload image'}</span>
                  <input id={'si-img-'+sf.name} type="file" accept="image/*" style={{display:'none'}}
                    onChange={async e => {
                      const file = e.target.files?.[0]; if (!file) return;
                      const fd = new FormData(); fd.append('image', file);
                      try {
                        const res = await fetch(process.env.REACT_APP_API_URL+'api/upload', {method:'POST',body:fd});
                        const data = await res.json();
                        if (data.url) set(sf.name, data.url);
                      } catch(err) { alert('Upload failed'); }
                    }} />
                </div>
                {vals[sf.name] && <img src={vals[sf.name]} alt="" style={{width:200,height:130,objectFit:'cover',borderRadius:6,marginTop:6}} onError={e=>e.target.style.display='none'} />}
                {vals[sf.name] && (
                  <details className="image-seo-details" style={{marginTop:6}}>
                    <summary style={{fontSize:11,color:'var(--muted2)',cursor:'pointer',userSelect:'none'}}>Alt text, caption, description, credit ▾</summary>
                    <div style={{marginTop:8,display:'flex',flexDirection:'column',gap:8}}>
                      <div>
                        <label className="mlabel" style={{fontSize:10}}>Alt Text</label>
                        <input className="minput" style={{fontSize:12}} value={vals[sf.name + '_alt']||''} onChange={e => set(sf.name + '_alt', e.target.value)} placeholder="Describe this image..." />
                      </div>
                      <div>
                        <label className="mlabel" style={{fontSize:10}}>Caption</label>
                        <input className="minput" style={{fontSize:12}} value={vals[sf.name + '_caption']||''} onChange={e => set(sf.name + '_caption', e.target.value)} placeholder="Text shown under the image..." />
                      </div>
                      <div>
                        <label className="mlabel" style={{fontSize:10}}>Description</label>
                        <textarea className="minput" rows={2} style={{fontSize:12}} value={vals[sf.name + '_description']||''} onChange={e => set(sf.name + '_description', e.target.value)} placeholder="Longer internal/SEO description..." />
                      </div>
                      <div>
                        <label className="mlabel" style={{fontSize:10}}>Credit / Attribution</label>
                        <input className="minput" style={{fontSize:12}} value={vals[sf.name + '_credit']||''} onChange={e => set(sf.name + '_credit', e.target.value)} placeholder="Photographer or source credit..." />
                      </div>
                    </div>
                  </details>
                )}
              </div>
            ) : sf.type === 'file' ? (
              <div>
                <div style={{display:'flex',alignItems:'center',gap:8,padding:'8px 12px',border:'1px dashed var(--border2)',borderRadius:'var(--r-sm)',background:'var(--bg)',cursor:'pointer'}}
                  onClick={() => document.getElementById('si-file-'+sf.name)?.click()}>
                  <span style={{fontSize:12,color:'var(--muted2)'}}>📎 {vals[sf.name] ? 'Replace file' : 'Upload file'}</span>
                  <input id={'si-file-'+sf.name} type="file" style={{display:'none'}}
                    onChange={async e => {
                      const file = e.target.files?.[0]; if (!file) return;
                      const fd = new FormData(); fd.append('image', file);
                      try {
                        const res = await fetch(process.env.REACT_APP_API_URL+'api/upload', {method:'POST',body:fd});
                        const data = await res.json();
                        if (data.url) set(sf.name, data.url);
                      } catch(err) { alert('Upload failed'); }
                    }} />
                </div>
                {vals[sf.name] && <div style={{fontSize:11,color:'var(--muted)',marginTop:6,padding:'6px 8px',background:'var(--bg)',borderRadius:4,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>✓ {vals[sf.name].split('/').pop()}</div>}
              </div>
            ) : (
              <input className="minput" type={sf.type==='number'?'number':sf.type==='date'?'date':'text'}
                value={vals[sf.name]||''} onChange={e => set(sf.name, sf.type==='number' ? parseFloat(e.target.value) || 0 : e.target.value)}
                placeholder={`Enter ${sf.label}...`} />
            )}
          </div>
        ))}
        <div className="mactions">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-solid" onClick={() => onSave(vals)}>Done</button>
        </div>
      </div>
    </div>
  );
}

function EditSubFieldsModal({ field, onConfirm, onClose }) {
  const SIMPLE_TYPES = ['string','text','number','image','video','file','boolean','date','html'];
  const [subFields, setSubFields] = React.useState(field.subFields || []);
  const [sfName, setSfName] = React.useState('');
  const [sfType, setSfType] = React.useState('string');
  const [editingSubFieldLabel, setEditingSubFieldLabel] = React.useState(null);
  const [editingLabelValue, setEditingLabelValue] = React.useState('');
  const uid = () => Math.random().toString(36).slice(2, 8);

  const addSubField = () => {
    if (!sfName.trim()) return;
    setSubFields(p => [...p, { id: uid(), name: sfName.trim(), label: sfName.trim(), type: sfType }]);
    setSfName('');
    setSfType('string');
  };

  const handleUpdateSubFieldLabel = (newLabel) => {
    if (!editingSubFieldLabel || !newLabel.trim()) {
      alert('Label cannot be empty');
      return;
    }
    const updated = { ...editingSubFieldLabel, label: newLabel.trim() };
    setSubFields(p => p.map(sf =>
      sf.id === editingSubFieldLabel.id ? updated : sf
    ));
    setEditingSubFieldLabel(null);
  };

  return (
    <div className="overlay" onClick={onClose}>
      <div className="modal" style={{width:480}} onClick={e => e.stopPropagation()}>
        <div className="modal-title">Edit Sub-fields — <span className="accent">{field.label}</span></div>
        <p className="modal-sub">Add or remove sub-fields for this array.</p>
        <div className="sf-builder">
          <div className="sf-row">
            <input className="minput" style={{flex:1}} value={sfName}
              onChange={e => setSfName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addSubField()}
              placeholder="New sub-field name..." autoFocus />
            <select className="mselect" value={sfType} onChange={e => setSfType(e.target.value)}>
              {SIMPLE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <button className="btn-solid" style={{padding:'9px 16px'}} onClick={addSubField}>+</button>
          </div>
          {subFields.length > 0 && (
            <div className="sf-list">
              {subFields.map((sf, i) => (
                <div key={sf.id} className="sf-item">
                  <span className="sf-name">{sf.label}</span>
                  <span className="badge">{sf.type}</span>
                  <button className="edit-label-btn" onClick={() => {setEditingSubFieldLabel(sf); setEditingLabelValue(sf.label);}} title="Edit label">✎</button>
                  <button className="rm-btn" onClick={() => setSubFields(p => p.filter((_,j) => j !== i))}>×</button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="mactions">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-solid" onClick={() => onConfirm(field.id, subFields)} disabled={subFields.length === 0}>
            Save Sub-fields
          </button>
        </div>

        {editingSubFieldLabel && (
          <Modal onClose={() => setEditingSubFieldLabel(null)}>
            <div className="modal-title">Edit Sub-field Label</div>
            <label className="mlabel">Display Name</label>
            <input className="minput" value={editingLabelValue} onChange={e => setEditingLabelValue(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleUpdateSubFieldLabel(editingLabelValue)}
              placeholder="Enter sub-field display name" autoFocus />
            <div style={{fontSize:11,color:'var(--muted)',marginTop:8}}>
              Field key "{editingSubFieldLabel.name}" will remain unchanged
            </div>
            <div className="mactions">
              <button className="btn-ghost" onClick={() => setEditingSubFieldLabel(null)}>Cancel</button>
              <button className="btn-solid" onClick={() => handleUpdateSubFieldLabel(editingLabelValue)}>Update</button>
            </div>
          </Modal>
        )}
      </div>
    </div>
  );
}

export default function App({ user, userRole = "admin", project, onBack }) {
  const [pages, setPages]               = useState([]);
  const [activePage, setActivePage]     = useState(null);
  const [docTree, setDocTree]           = useState(null);
  const [navPath, setNavPath]           = useState([]);
  const [rightPane, setRightPane]       = useState(null);
  const [modal, setModal]               = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [editingSchema, setEditingSchema] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [localData, setLocalData] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [editingSingleItem, setEditingSingleItem] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving]             = useState(false);
  const [showAI, setShowAI]             = useState(false);
  const [tab, setTab]                   = useState('editor');
  const [topSections, setTopSections] = useState([]);
  const [autoSaveStatus, setAutoSaveStatus] = useState(null);
  const [editingFieldLabel, setEditingFieldLabel] = useState(null);
  const [editingSectionLabel, setEditingSectionLabel] = useState(null);
  const [pendingOpenDocId, setPendingOpenDocId] = useState(null); // blog post to auto-open after load
  const [linkCopied, setLinkCopied] = useState(false); // "Copy Link" indicator, kept separate from autoSaveStatus
  const [activeSection, setActiveSection] = useState('pages'); // 'pages' | 'forms'

  // ── Resizable middle panel (pages/sections list) ──
  const [navPanelWidth, setNavPanelWidth] = useState(260);
  const navPanelRef = React.useRef(null);
  const isResizingRef = React.useRef(false);

  const handleResizeStart = (e) => {
    e.preventDefault();
    isResizingRef.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizingRef.current || !navPanelRef.current) return;
      const rect = navPanelRef.current.getBoundingClientRect();
      const newWidth = e.clientX - rect.left;
      setNavPanelWidth(Math.min(600, Math.max(180, newWidth)));
    };
    const handleMouseUp = () => {
      if (!isResizingRef.current) return;
      isResizingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  // ── Permission tiers ──────────────────────────────
  // admin  : full control (add / edit / delete everything, schemas + documents + members)
  // editor : can only EDIT existing values. cannot add or delete pages, sections, fields, or array items.
  // viewer : fully read-only. no add / edit / delete of anything.
  const isAdmin = userRole === 'admin';
  // Who's currently editing, used to stamp lastEditedBy/lastEditedAt onto
  // a section's data on every save — shown in the section list and editor
  // header so it's visible which admin/editor last touched a section.
  const editorName = user?.name || user?.email || user?.username || 'Unknown';
  const stampEdit = (data) => ({ ...data, lastEditedBy: editorName, lastEditedAt: new Date().toISOString() });
  const isViewer = userRole === 'viewer';
  const canEdit = userRole !== 'viewer';        // admin + editor can edit existing values
  const canAddOrDelete = userRole === 'admin';   // only admin can create or delete things

  const autoSaveTimer = React.useRef(null);
  const localDataRef = React.useRef({});

  // ── Live refs so deferred/async saves never read stale state ──
  // (fixes the delete-doesn't-persist bug: the debounced save used to
  //  close over an old `rightPane`/`docTree` and re-save the removed field)
  const rightPaneRef = React.useRef(null);
  const docTreeRef   = React.useRef(null);
  useEffect(() => { rightPaneRef.current = rightPane; }, [rightPane]);
  useEffect(() => { docTreeRef.current = docTree; }, [docTree]);

  useEffect(() => {
    if (!project?._id) return;
    API.get('/api/schemas?projectId=' + project._id).then(r => setPages(r.data)).catch(() => {});
  }, [project?._id]);

  useEffect(() => {
    if (!activePage) return;
    API.get(`/api/documents?schemaId=${activePage._id}`).then(r => {
      const docs = r.data.map(doc => ({
        _id: doc._id,
        title: doc.title,
        label: doc.label,
        published: doc.published,
        tree: doc.data?.tree || { id: doc._id, name: doc.title, label: doc.title, children: [], fields: [], data: {} }
      }));
      setTopSections(docs);
      setDocTree(null);
      setNavPath([]);
      setRightPane(null);
    }).catch(() => {});
  }, [activePage]);

  const currentNode = docTree ? getNodeByPath(docTree.tree, navPath) : null;

  const breadcrumb = () => {
    if (!docTree) return [];
    const crumbs = [{ label: docTree.title, path: [] }];
    let node = docTree.tree;
    for (const id of navPath) {
      node = (node.children || []).find(c => c.id === id);
      if (!node) break;
      crumbs.push({ label: node.label || node.name, path: crumbs[crumbs.length - 1].path.concat(id) });
    }
    return crumbs;
  };

  const updateTree = (path, updater) => {
    setDocTree(prev => {
      const newTree = updateNodeByPath(prev.tree, path, updater);
      const updated = { ...prev, tree: newTree };
      setTopSections(ts => ts.map(s => s._id === prev._id ? updated : s));
      return updated;
    });
  };

  // ── Shared persist helper: writes the given tree to the server now ──
  const persistTree = async (updatedTree, opts = {}) => {
    const dt = docTreeRef.current || docTree;
    if (!dt) return;
    setAutoSaveStatus('saving');
    try {
      await API.put('/api/documents/' + dt._id, {
        title: dt.title,
        data: { tree: updatedTree },
        published: opts.published !== undefined ? opts.published : dt.published
      });
      setAutoSaveStatus('saved');
      setTimeout(() => setAutoSaveStatus(null), 2000);
    } catch (e) {
      console.error('Save failed:', e);
      setAutoSaveStatus(null);
      alert('Failed to save changes to the server.');
    }
  };

  const handleAddPage = async (name) => {
    if (!canAddOrDelete) return;
    try {
      const res = await API.post('/api/schemas', { name, slug: slugify(name), color: COLORS[pages.length % COLORS.length], fields: [], projectId: project?._id || null });
      setPages(p => [...p, res.data]);
      setActivePage(res.data);
    } catch(e) { alert('Error'); }
    setModal(null);
  };

  // One-click blog post: ensures a "Blog" page exists, creates a post
  // pre-loaded with all blog fields, and opens it in the editor.
  const handleNewBlogPost = async (name) => {
    if (!canAddOrDelete) return;
    const postTitle = (name && name.trim()) || 'Untitled Post';
    try {
      // 1. find the Blog page in memory, or on the server, or create it
      let blogPage = pages.find(p => p.slug === 'blog');
      if (!blogPage) {
        try {
          const res = await API.post('/api/schemas', {
            name: 'Blog', slug: 'blog',
            color: COLORS[pages.length % COLORS.length],
            fields: [], projectId: project?._id || null
          });
          blogPage = res.data;
          setPages(p => [...p, blogPage]);
        } catch (schemaErr) {
          // maybe the Blog page already exists on the server — reload and reuse it
          const all = await API.get('/api/schemas?projectId=' + (project?._id || ''));
          blogPage = (all.data || []).find(p => p.slug === 'blog');
          if (!blogPage) throw schemaErr;
          setPages(all.data);
        }
      }
      if (!blogPage?._id) { alert('Could not resolve the Blog page (no _id returned).'); return; }

      // 2. build a post already carrying every blog field, titled from the name
      const fields = BLOG_TEMPLATE.map(f => ({ id: uid(), ...f }));
      const data = {};
      fields.forEach(f => { data[f.name] = f.type === 'array' ? [] : ''; });
      data.title = postTitle;            // pre-fill Title + Slug from the entered name
      data.slug  = slugify(postTitle);
      const newTree = { id: uid(), _key: 'post-' + uid(), name: postTitle, label: postTitle, children: [], fields, data };

      // 3. save it as a document under the Blog page.
      // NOTE: top-level `fields` must stay [] — the backend validates its
      // type against a strict enum that has no `html`. The real blog fields
      // live inside data.tree.fields (unvalidated), exactly like normal sections.
      const res = await API.post('/api/documents', {
        schemaId: blogPage._id, schemaSlug: blogPage.slug,
        title: postTitle, fields: [], data: { tree: newTree }, published: false
      });
      const doc = { _id: res.data._id, title: postTitle, published: false, tree: newTree };
      setModal(null);

      // 4. open the new post in the editor with all fields ready to fill
      if (activePage?.slug === 'blog') {
        setTopSections(p => [...p, doc]);
        handleTopSectionClick(doc);
      } else {
        setActivePage(blogPage);
        setPendingOpenDocId(doc._id); // load effect fetches it, then auto-opens
      }
    } catch(e) {
      console.error('New blog post failed:', e);
      const detail = e?.response?.data?.message || e?.response?.data?.error || e?.message || 'unknown error';
      const where = e?.config?.url ? ` (${e.config.method?.toUpperCase()} ${e.config.url})` : '';
      alert('Error creating blog post' + where + ': ' + detail);
    }
  };

  const handleAddTopSection = async (name, key, onError) => {
    if (!canAddOrDelete) return;
    if (!activePage) return;
    const existingKeys = topSections.map(s => s.tree?._key || '');
    if (existingKeys.includes(key)) {
      if (onError) onError('Key "' + key + '" already exists. Please choose a different one.');
      return;
    }
    const newTree = { id: uid(), _key: key, name, label: name, children: [], fields: [], data: {} };
    try {
      const res = await API.post('/api/documents', {
        schemaId: activePage._id, schemaSlug: activePage.slug,
        title: name, fields: [], data: { tree: newTree }, published: false
      });
      const doc = { _id: res.data._id, title: name, published: false, tree: newTree };
      setTopSections(p => [...p, doc]);
    } catch(e) { alert('Error'); }
    setModal(null);
  };

  const handleDeletePage = async (pageId) => {
    if (!canAddOrDelete) return;
    if (!window.confirm('Delete this page and ALL its sections?')) return;
    try {
      const docs = await API.get('/api/documents?schemaId=' + pageId);
      await Promise.all(docs.data.map(d => API.delete('/api/documents/' + d._id)));
      await API.delete('/api/schemas/' + pageId);
      setPages(p => p.filter(pg => pg._id !== pageId));
      if (activePage?._id === pageId) {
        setActivePage(null);
        setDocTree(null);
        setNavPath([]);
        setRightPane(null);
        setTopSections([]);
      }
    } catch(e) { alert('Error deleting page'); }
  };

  const handleDeleteTopSection = async (id) => {
    if (!canAddOrDelete) return;
    if (!window.confirm('Delete this section?')) return;
    await API.delete(`/api/documents/${id}`);
    setTopSections(p => p.filter(s => s._id !== id));
    if (docTree?._id === id) { setDocTree(null); setNavPath([]); setRightPane(null); }
  };

  const handleTopSectionClick = (s) => {
    API.get('/api/documents/' + s._id).then(r => {
      const doc = r.data;
      const tree = doc.data?.tree || { id: doc._id, name: doc.title, label: doc.title, children: [], fields: [], data: {} };
      const freshDoc = { _id: doc._id, title: doc.title, published: doc.published, tree };
      setDocTree(freshDoc);
      setNavPath([]);
      if ((tree.fields || []).length > 0) {
        setRightPane({ ...tree, path: [] });
      } else {
        setRightPane(null);
      }
    }).catch(() => {
      setDocTree(s);
      setNavPath([]);
      setRightPane(null);
    });
  };

  // After the Blog page's sections load, auto-open the post we just created.
  useEffect(() => {
    if (!pendingOpenDocId) return;
    const doc = topSections.find(s => s._id === pendingOpenDocId);
    if (doc) {
      handleTopSectionClick(doc);
      setPendingOpenDocId(null);
    }
  }, [topSections, pendingOpenDocId]);

  const handleNavigateInto = (child) => {
    setNavPath(p => [...p, child.id]);
    setRightPane(null);
  };

  const handleSectionClick = (child) => {
    const freshChild = docTree ? getNodeByPath(docTree.tree, [...navPath, child.id]) || child : child;
    const hasChildren = (freshChild.children || []).length > 0;
    const hasFields = (freshChild.fields || []).length > 0;
    if (hasChildren) {
      setNavPath(p => [...p, child.id]);
      setRightPane(null);
    } else if (hasFields) {
      setRightPane({ ...freshChild, path: [...navPath, child.id] });
    } else {
      setNavPath(p => [...p, child.id]);
      setRightPane(null);
    }
  };

  const handleAddSubSection = (name, key) => {
    if (!canAddOrDelete) return;
    const newChild = { id: uid(), _key: key, name, label: name, children: [], fields: [], data: {} };
    updateTree(navPath, node => ({ ...node, children: [...(node.children || []), newChild] }));
    setModal(null);
  };

  const handleAddField = async (field) => {
    if (!canAddOrDelete) return;
    if (!docTree) { setModal(null); return; }
    const targetPath = rightPane ? rightPane.path : navPath;
    const defaultVal = field.type==='array'?[]:field.type==='boolean'?false:field.type==='number'?0:'';
    const updater = node => ({
      ...node,
      fields: [...(node.fields || []), field],
      data: stampEdit({ ...(node.data || {}), [field.name]: defaultVal })
    });
    const updatedTree = updateNodeByPath(docTree.tree, targetPath, updater);
    updateTree(targetPath, updater);
    const targetNode = getNodeByPath(updatedTree, targetPath);
    setRightPane({ ...targetNode, path: targetPath });
    setModal(null);
    // persist immediately so a freshly-added field survives a refresh
    await persistTree(updatedTree);
  };

  const handleUpdateSubFields = async (fieldId, newSubFields) => {
    if (!canAddOrDelete) return;
    if (!rightPane || !docTree) return;
    const updatedFields = (rightPane.fields || []).map(f =>
      f.id === fieldId ? { ...f, subFields: newSubFields } : f
    );

    const updatedTree = updateNodeByPath(docTree.tree, rightPane.path, node => ({
      ...node,
      fields: updatedFields
    }));

    updateTree(rightPane.path, node => ({ ...node, fields: updatedFields }));
    setRightPane(prev => ({ ...prev, fields: updatedFields }));
    try {
      await API.put('/api/documents/' + docTree._id, {
        title: docTree.title,
        data: { tree: updatedTree }
      });
    } catch(e) { console.error('Save error', e); }
    setEditingSchema(null);
  };

  const handleReorder = async (newOrder) => {
    if (isViewer) return;
    setTopSections(newOrder);
    try {
      await API.post('/api/documents/reorder', { ids: newOrder.map(s => s._id) });
    } catch(e) { console.error('Reorder failed', e); }
  };

  useEffect(() => {
    if (rightPane) {
      setLocalData(rightPane.data || {});
      setHasChanges(false);
      setAutoSaveStatus(null);
      clearTimeout(autoSaveTimer.current);
    }
  }, [rightPane?.id]);

  useEffect(() => {
    localDataRef.current = localData;
  }, [localData]);

  const handleLocalChange = (fieldName, value) => {
    if (!canEdit) return;
    setLocalData(prev => ({ ...prev, [fieldName]: value }));
    setHasChanges(true);
    clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      triggerAutoSave();
    }, 1500);
  };

  const triggerAutoSave = async () => {
    if (!canEdit) return;
    // read the *current* panel/doc from refs, never a stale closure
    const rp = rightPaneRef.current;
    const dt = docTreeRef.current;
    if (!rp || !dt) return;
    const currentLocalData = localDataRef.current;
    setAutoSaveStatus('saving');
    try {
      updateTree(rp.path, node => ({ ...node, data: stampEdit({ ...(node.data || {}), ...currentLocalData }) }));
      setRightPane(prev => ({ ...prev, data: stampEdit({ ...(prev.data || {}), ...currentLocalData }) }));
      const updatedTree = updateNodeByPath(dt.tree, rp.path, node => ({
        ...node,
        fields: rp.fields,
        data: stampEdit({ ...(node.data || {}), ...currentLocalData })
      }));
      await API.put('/api/documents/' + dt._id, {
        title: dt.title,
        data: { tree: updatedTree },
        published: dt.published
      });
      setHasChanges(false);
      setAutoSaveStatus('saved');
      setTimeout(() => setAutoSaveStatus(null), 2000);
    } catch(e) {
      console.error('Auto-save failed:', e);
      setAutoSaveStatus(null);
    }
  };

  const handleSaveInline = async () => {
    if (!canEdit) return;
    clearTimeout(autoSaveTimer.current);
    await triggerAutoSave();
  };

  const handleDiscardInline = () => {
    setLocalData(rightPane?.data || {});
    setHasChanges(false);
  };

  const handleFieldSave = (fieldName, value) => {
    if (!canEdit) return;
    if (!rightPane) return;
    updateTree(rightPane.path, node => ({ ...node, data: { ...(node.data || {}), [fieldName]: value } }));
    setRightPane(prev => ({ ...prev, data: { ...(prev.data || {}), [fieldName]: value } }));
  };

  const handleDeleteField = async (fieldId) => {
    if (!canAddOrDelete) return;
    if (!rightPane || !docTree) return;
    if (!window.confirm('Remove this field?')) return;

    const fieldName = (rightPane.fields || []).find(f => f.id === fieldId)?.name;
    const newFields = (rightPane.fields || []).filter(f => f.id !== fieldId);

    // updater drops the field definition *and* its stored value
    const updater = node => {
      const d = { ...(node.data || {}) };
      if (fieldName) delete d[fieldName];
      return { ...node, fields: newFields, data: stampEdit(d) };
    };

    // build the fresh tree synchronously, update local state, then save NOW
    const updatedTree = updateNodeByPath(docTree.tree, rightPane.path, updater);
    updateTree(rightPane.path, updater);
    setRightPane(prev => {
      const d = { ...(prev.data || {}) };
      if (fieldName) delete d[fieldName];
      return { ...prev, fields: newFields, data: d };
    });
    setLocalData(prev => {
      const nd = { ...prev };
      if (fieldName) delete nd[fieldName];
      return nd;
    });
    setHasChanges(false);
    clearTimeout(autoSaveTimer.current);   // cancel any pending stale autosave
    await persistTree(updatedTree);
  };

  const handleDeleteChild = async (childId) => {
    if (!canAddOrDelete) return;
    if (!docTree) return;
    if (!window.confirm('Delete this section?')) return;

    const updater = node => ({ ...node, children: (node.children || []).filter(c => c.id !== childId) });
    const updatedTree = updateNodeByPath(docTree.tree, navPath, updater);
    updateTree(navPath, updater);
    if (rightPane?.id === childId) setRightPane(null);
    await persistTree(updatedTree);
  };

  const handleSave = async (publish = false) => {
    if (!canEdit) return;
    if (!docTree) return;
    setSaving(true);
    try {
      const res = await API.put(`/api/documents/${docTree._id}`, {
        title: docTree.title,
        data: { tree: docTree.tree },
        published: publish || docTree.published
      });
      setDocTree(prev => ({ ...prev, published: res.data.published }));
      setTopSections(ts => ts.map(s => s._id === docTree._id ? { ...s, published: res.data.published, tree: docTree.tree } : s));
      alert('Saved successfully!');
    } catch(e) { alert('Error saving: ' + e.message); }
    setSaving(false);
  };

  const handleUnpublish = async () => {
    if (!canEdit) return;
    if (!docTree) return;
    setSaving(true);
    try {
      const res = await API.put(`/api/documents/${docTree._id}`, {
        title: docTree.title,
        data: { tree: docTree.tree },
        published: false
      });
      setDocTree(prev => ({ ...prev, published: false }));
      setTopSections(ts => ts.map(s => s._id === docTree._id ? { ...s, published: false } : s));
    } catch(e) { alert('Error: ' + e.message); }
    setSaving(false);
  };

  const handleUpdateFieldLabel = async (newLabel) => {
    if (!canEdit) return;
    if (!rightPane || !docTree || !editingFieldLabel) return;
    if (!newLabel.trim()) {
      alert('Label cannot be empty');
      return;
    }
    try {
      const updatedFields = (rightPane.fields || []).map(f =>
        f.id === editingFieldLabel.id ? { ...f, label: newLabel.trim() } : f
      );

      const updatedTree = updateNodeByPath(docTree.tree, rightPane.path, node => ({
        ...node,
        fields: updatedFields
      }));

      updateTree(rightPane.path, node => ({ ...node, fields: updatedFields }));
      setRightPane(prev => ({ ...prev, fields: updatedFields }));

      await API.put(`/api/documents/${docTree._id}`, {
        title: docTree.title,
        data: { tree: updatedTree }
      });
      setEditingFieldLabel(null);
    } catch(e) { alert('Error updating field label: ' + e.message); }
  };

  const handleUpdateSectionLabel = async (newLabel) => {
    if (!canEdit) return;
    if (!editingSectionLabel) return;
    if (!newLabel.trim()) {
      alert('Label cannot be empty');
      return;
    }
    try {
      if (editingSectionLabel.isTopSection) {
        // Update parent section label only (keep title unchanged)
        await API.put(`/api/documents/${editingSectionLabel._id}`, {
          title: editingSectionLabel.title,
          data: editingSectionLabel.data || { tree: editingSectionLabel.tree },
          published: editingSectionLabel.published,
          label: newLabel.trim()
        });
        setTopSections(ts => ts.map(s => s._id === editingSectionLabel._id ? { ...s, label: newLabel.trim() } : s));
      } else {
        // Update child section label
        if (!docTree) return;
        const updatedTree = updateNodeByPath(docTree.tree, editingSectionLabel.path, node => ({
          ...node,
          label: newLabel.trim()
        }));

        updateTree(editingSectionLabel.path, node => ({ ...node, label: newLabel.trim() }));

        await API.put(`/api/documents/${docTree._id}`, {
          title: docTree.title,
          data: { tree: updatedTree }
        });
      }
      setEditingSectionLabel(null);
    } catch(e) { alert('Error updating section label: ' + e.message); }
  };

  const crumbs = breadcrumb();

  return (
    <div className="app">
      <aside className="sidebar">
        <div className="brand">
          <div className="brand-mark">CMS</div>
          <div className="brand-info">
            <span className="brand-name">Sunkind CMS</span>
            {project && <span className="brand-project">{project.name}</span>}
          </div>
        </div>
        <div className="sidebar-label">PAGES</div>
        <nav className="page-nav">
          {pages.map(p => (
            <div key={p._id} className={`page-item ${activePage?._id === p._id && activeSection === 'pages' ? 'active' : ''}`}
              onClick={() => { setActiveSection('pages'); setActivePage(p); setDocTree(null); setNavPath([]); setRightPane(null); }}>
              <div className="page-dot" style={{background: p.color}} />
              <span style={{flex:1}}>{p.name}</span>
              {canAddOrDelete && <button className="page-del-btn" onClick={e => { e.stopPropagation(); handleDeletePage(p._id); }} title="Delete page">×</button>}
            </div>
          ))}
          {pages.length === 0 && <div className="nav-empty">No pages yet</div>}
        </nav>
        <button
          className={`sidebar-btn ${activeSection === 'forms' ? 'active' : ''}`}
          onClick={() => setActiveSection('forms')}
        >
          ▤ Forms
        </button>
        {canAddOrDelete && <button className="sidebar-btn" onClick={() => setModal('addPage')}>+ New Page</button>}
        {canAddOrDelete && <button className="sidebar-btn blog-sidebar-btn" onClick={() => setModal('addBlogPost')}>✎ New Blog Post</button>}
        {/* {activePage && canEdit && <button className="sidebar-btn ai-sidebar-btn" onClick={() => setShowAI(true)}>✦ AI Builder</button>} */}
        {canAddOrDelete && <BackupPanel API={API} />}
      </aside>

      {activeSection === 'forms' ? (
        <FormsPanel API={API} canAddOrDelete={canAddOrDelete} />
      ) : (
      <>
      <section
        className="nav-panel"
        ref={navPanelRef}
        style={{ width: navPanelWidth, flexShrink: 0, position: 'relative' }}
      >
        <div className="breadcrumb-bar">
          <div className="bc-left">
            {(docTree) && (
              <button className="back-btn" onClick={() => {
                if (navPath.length > 0) {
                  setNavPath(p => p.slice(0, -1));
                  setRightPane(null);
                } else {
                  setDocTree(null);
                  setNavPath([]);
                  setRightPane(null);
                }
              }}>‹</button>
            )}
            {docTree ? (
              <div className="breadcrumbs">
                {crumbs.map((c, i) => (
                  <span key={i} className="bc-item">
                    <button className={`bc-btn ${i === crumbs.length - 1 ? 'active' : ''}`}
                      onClick={() => { setNavPath(c.path); setRightPane(null); }}>
                      {c.label}
                    </button>
                    {i < crumbs.length - 1 && <span className="bc-sep">›</span>}
                  </span>
                ))}
              </div>
            ) : (
              <div className="bc-placeholder">{activePage ? activePage.name : 'Select a page'}</div>
            )}
          </div>
          {docTree && canAddOrDelete && (
            <button className="icon-btn" onClick={() => setModal('addSection')} title="Add section">+</button>
          )}
          {!docTree && activePage && canAddOrDelete && (
            <button className="icon-btn" onClick={() => setModal('addTopSection')} title="Add section">+</button>
          )}
        </div>

        <div className="nav-list">
          {!activePage && <div className="nav-empty-msg">← Select a page</div>}
          {activePage && !docTree && (
            <>
              {topSections.length === 0 && <div className="nav-empty-msg">No sections yet.<br/>Click + to add one.</div>}
              {topSections.map((s, i) => (
                <div key={s._id}
                  className={`nav-item ${dragOverId === s._id ? 'drag-over' : ''}`}
                  draggable={!isViewer}
                  onDragStart={e => e.dataTransfer.setData('id', s._id)}
                  onDragOver={e => { e.preventDefault(); setDragOverId(s._id); }}
                  onDragLeave={() => setDragOverId(null)}
                  onDrop={e => {
                    e.preventDefault();
                    setDragOverId(null);
                    const draggedId = e.dataTransfer.getData('id');
                    if (draggedId === s._id) return;
                    const from = topSections.findIndex(x => x._id === draggedId);
                    const to = topSections.findIndex(x => x._id === s._id);
                    const newOrder = [...topSections];
                    const [moved] = newOrder.splice(from, 1);
                    newOrder.splice(to, 0, moved);
                    handleReorder(newOrder);
                  }}
                  onClick={() => handleTopSectionClick(s)}
                  title={s.tree?.data?.lastEditedBy ? `Last updated by ${s.tree.data.lastEditedBy} on ${new Date(s.tree.data.lastEditedAt).toLocaleString()}` : undefined}>
                  <span className="drag-handle" title="Drag to reorder">⠿</span>
                  <span className="nav-item-name">{s.label || s.title}</span>
                  <span className={`pill ${s.published ? 'pub' : 'draft'}`}>{s.published ? 'pub' : 'draft'}</span>
                  {canEdit && <button className="edit-label-btn" onClick={e => { e.stopPropagation(); setEditingSectionLabel({...s, isTopSection: true}); }} title="Edit label">✎</button>}
                  {canAddOrDelete && <button className="rm-btn" onClick={e => { e.stopPropagation(); handleDeleteTopSection(s._id); }}>×</button>}
                </div>
              ))}
            </>
          )}
          {docTree && currentNode && (
            <>
              {(currentNode.children || []).length === 0 && (
                <div className="nav-empty-msg">
                  No sub-sections here.<br/>
                  Click + to add one,<br/>
                  or add data fields on the right.
                </div>
              )}
              {(currentNode.children || []).map(child => {
                const freshChild = docTree ? getNodeByPath(docTree.tree, [...navPath, child.id]) || child : child;
                return (
                  <div key={child.id}
                    className={`nav-item ${rightPane?.id === child.id ? 'selected' : ''}`}
                    onClick={() => handleSectionClick(child)}>
                    <span className="nav-item-icon">
                      {(freshChild.children || []).length > 0 ? '⊟' : (freshChild.fields || []).length > 0 ? '◈' : '⊟'}
                    </span>
                    <span className="nav-item-name">{freshChild.label || freshChild.name}</span>
                    {(freshChild.children || []).length > 0 && <span className="nav-item-arrow">›</span>}
                    {(freshChild.fields || []).length > 0 && <span className="nav-item-count">{freshChild.fields.length}</span>}
                    {canEdit && <button className="edit-label-btn" onClick={e => { e.stopPropagation(); setEditingSectionLabel({...freshChild, path: [...navPath, child.id]}); }} title="Edit label">✎</button>}
                    {canAddOrDelete && <button className="rm-btn" onClick={e => { e.stopPropagation(); handleDeleteChild(child.id); }}>×</button>}
                  </div>
                );
              })}
              {(currentNode.children || []).length === 0 && canAddOrDelete && (
                <button className="add-data-btn" onClick={() => setModal('addField')}>+ Add Data Field</button>
              )}
            </>
          )}
        </div>
        <div
          onMouseDown={handleResizeStart}
          title="Drag to resize"
          style={{
            position: 'absolute',
            top: 0,
            right: -3,
            width: 6,
            height: '100%',
            cursor: 'col-resize',
            zIndex: 20,
            background: 'transparent',
          }}
        />
      </section>

      <main className="editor">
        <div className="editor-bar">
          <div className="tabs">
            {onBack && <button className="back-to-dash" onClick={onBack}>← Projects</button>}
            <button className={`tab ${tab === 'editor' ? 'act' : ''}`} onClick={() => setTab('editor')}>Editor</button>
            <button className={`tab ${tab === 'api' ? 'act' : ''}`} onClick={() => setTab('api')}>API</button>
          </div>
          {docTree && tab === 'editor' && canEdit && (
            <div className="editor-actions" style={{display:'flex',alignItems:'center',flexWrap:'nowrap',gap:8,minWidth:0}}>
              {docTree.published && activePage?.slug === 'blog' && (
                <div style={{
                  display:'flex', alignItems:'center', gap:6, marginRight:8,
                  flexWrap:'nowrap', minWidth:0, flex:'1 1 auto'
                }}>
                  {/* Read-only, non-editable, non-selectable display of the public URL */}
                  <span
                    title={getBlogPostUrl(docTree)}
                    style={{
                      fontSize:12, fontFamily:'var(--mono)', padding:'6px 10px',
                      border:'1px solid var(--border2)', borderRadius:'var(--r-sm)',
                      background:'var(--bg)', color:'var(--muted2)',
                      minWidth:0, flex:'1 1 160px', maxWidth:280,
                      overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
                      userSelect:'none', cursor:'default'
                    }}
                  >
                    {getBlogPostUrl(docTree)}
                  </span>
                  <button
                    className="btn-ghost"
                    style={{ whiteSpace:'nowrap', flexShrink:0 }}
                    onClick={() => {
                      navigator.clipboard.writeText(getBlogPostUrl(docTree));
                      setLinkCopied(true);
                      setTimeout(() => setLinkCopied(false), 1500);
                    }}
                    title="Copy link"
                  >
                    {linkCopied ? '✓ Copied' : 'Copy Link'}
                  </button>
                  <a
                    className="btn-ghost"
                    href={getBlogPostUrl(docTree)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration:'none', whiteSpace:'nowrap', flexShrink:0 }}
                  >
                    Open ↗
                  </a>
                </div>
              )}
              {docTree.published && (
                <button className="btn-unpub" onClick={() => handleUnpublish()} disabled={saving}>
                  {saving ? 'Saving…' : 'Unpublish'}
                </button>
              )}
              <button className={`btn-pub ${docTree.published ? 'done' : ''}`}
                onClick={() => handleSave(true)} disabled={saving || docTree.published}>
                {docTree.published ? '✓ Published' : 'Publish'}
              </button>
            </div>
          )}
        </div>

        {tab === 'editor' && (
          <div className="editor-body">
            {!rightPane && (
              <div className="editor-empty">
                <div className="empty-icon">◈</div>
                <div>
                  {!activePage && 'Select a page to get started'}
                  {activePage && !docTree && 'Select a section from the panel'}
                  {docTree && !rightPane && 'Select a section that contains data fields'}
                </div>
              </div>
            )}

            {rightPane && (
              <>
                <div className="data-editor-header">
                  <div className="data-editor-title">
                    <span className="data-editor-icon">◈</span>
                    {rightPane.label || rightPane.name}
                  </div>
                  {canAddOrDelete && <button className="btn-add-field" onClick={() => setModal('addField')}>+ Add Field</button>}
                </div>

                {rightPane.data?.lastEditedBy && (
                  <div style={{fontSize:11,color:'var(--muted)',marginTop:-6,marginBottom:10}}>
                    Last updated by <strong>{rightPane.data.lastEditedBy}</strong> on {new Date(rightPane.data.lastEditedAt).toLocaleString()}
                  </div>
                )}

                {(rightPane.fields || []).length === 0 && (
                  <div className="no-fields">No fields yet. Click "+ Add Field" above.</div>
                )}

                {(hasChanges || autoSaveStatus) && (
                  <div className="inline-save-bar">
                    {autoSaveStatus === 'saving' && <span className="inline-save-msg">Auto-saving…</span>}
                    {autoSaveStatus === 'saved' && <span className="inline-save-msg" style={{color:'var(--accent)'}}>✓ Saved</span>}
                    {!autoSaveStatus && hasChanges && (
                      <>
                        <span className="inline-save-msg">Unsaved changes</span>
                        <button className="btn-ghost" onClick={handleDiscardInline}>Discard</button>
                        <button className="btn-solid" onClick={handleSaveInline}>Save</button>
                      </>
                    )}
                  </div>
                )}

                <div className="fields-list">
                  {(rightPane.fields || []).map(f => {
                    const val = localData[f.name];
                    const isArray = f.type === 'array';
                    const isImage = f.type === 'image';
                    const isText = f.type === 'text';
                    const isHtml = f.type === 'html';
                    const isBool = f.type === 'boolean';
                    const arrVal = localData[f.name] ?? (rightPane.data || {})[f.name];
                    return (
                      <div key={f.id || f.name} className="inline-field-row">
                        <div className="inline-field-header">
                          <div className="inline-field-label-row">
                            <span className="field-row-icon">{typeIcon(f.type)}</span>
                            <span className="field-row-name">{f.label}</span>
                            <span className="badge">{f.type === 'array' ? `array · ${(f.subFields||[]).length} sub-fields` : f.type}</span>
                            {canEdit && <button className="edit-label-btn" onClick={e => { e.stopPropagation(); setEditingFieldLabel(f); }} title="Edit label">✎</button>}
                          </div>
                          <div style={{display:'flex',gap:6}}>
                            {isArray && canAddOrDelete && <button className="edit-schema-btn" onClick={e => { e.stopPropagation(); setEditingSchema(f); }}>Edit Fields</button>}
                            {canAddOrDelete && <button className="rm-btn field-rm" onClick={e => { e.stopPropagation(); handleDeleteField(f.id); }}>×</button>}
                          </div>
                        </div>

                        {isArray && (
                          <div className="inline-array-area">
                            {Array.isArray(arrVal) && arrVal.length > 0 ? (
                              <div className="inline-cards-list">
                                {arrVal.map((item, idx) => (
                                  <div key={item._key || JSON.stringify(item)}
                                    className="inline-card-preview"
                                    draggable={canEdit}
                                    onDragStart={e => { if (!canEdit) { e.preventDefault(); return; } e.dataTransfer.setData('cardIdx', String(idx)); }}
                                    onDragOver={e => { if (canEdit) e.preventDefault(); }}
                                    onDrop={e => {
                                      e.preventDefault();
                                      if (!canEdit) return;
                                      const from = parseInt(e.dataTransfer.getData('cardIdx'), 10);
                                      const to = idx;
                                      if (from === to) return;
                                      const newArr = [...arrVal];
                                      const [moved] = newArr.splice(from, 1);
                                      newArr.splice(to, 0, moved);
                                      handleLocalChange(f.name, newArr);
                                    }}
                                    onClick={() => { if (canEdit) setEditingSingleItem({field: f, index: idx, item}); }}
                                    style={!canEdit ? { cursor: 'default' } : undefined}>
                                    <div style={{display:'flex',alignItems:'center',gap:8}}>
                                      {canEdit && <span className="drag-handle" title="Drag to reorder" onClick={e => e.stopPropagation()}>⠿</span>}
                                      <div style={{flex:1}}>
                                        {(() => {
                                          const entries = Object.entries(item).filter(([k]) => k !== '_key');
                                          // Prefer showing "title" (or "Title") specifically, so a
                                          // card's preview label is always the post title, never
                                          // whichever field happened to be entered/saved first.
                                          const titleEntry = entries.find(([k]) => k === 'title' || k === 'Title');
                                          const shown = titleEntry ? [titleEntry] : entries.slice(0, 1);
                                          return shown.map(([k, v]) => {
                                            const subField = (f.subFields || []).find(sf => sf.name === k);
                                            const label = subField?.label || k;
                                            return (
                                              <div key={k} className="inline-card-row">
                                                <span className="inline-card-key">{label}</span>
                                                <span className="inline-card-val">
                                                  {typeof v === 'string' && (v.startsWith('http')||v.includes('/ipfs/'))
                                                    ? <img src={v} alt="" style={{width:40,height:40,objectFit:'cover',borderRadius:4}} onError={e=>e.target.style.display='none'} />
                                                    : String(v).slice(0,60)}{String(v).length>60?'...':''}
                                                </span>
                                              </div>
                                            );
                                          });
                                        })()}
                                        {canEdit && <div className="inline-card-edit-hint">click to edit · drag to reorder</div>}
                                      </div>
                                      {canAddOrDelete && <button className="rm-btn" style={{flexShrink:0}} onClick={e => {
                                        e.stopPropagation();
                                        const newArr = arrVal.filter((_, j) => j !== idx);
                                        handleLocalChange(f.name, newArr);
                                      }}>×</button>}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="inline-array-empty">No items yet</div>
                            )}
                            {canAddOrDelete && (
                              <button className="inline-array-add-btn" onClick={() => setEditingField(f)}>+ Add Item</button>
                            )}
                          </div>
                        )}

                        {!isArray && canEdit && (
                          <div className="inline-field-input">
                            {f.type === 'video' ? (
                              <div>
                                <div style={{display:'flex',alignItems:'center',gap:8,padding:'7px 10px',border:'1px dashed var(--border2)',borderRadius:'var(--r-sm)',background:'var(--bg)',cursor:'pointer'}}
                                  onClick={() => document.getElementById('inline-vid-'+f.id)?.click()}>
                                  <span style={{fontSize:12,color:'var(--muted2)'}}>▶ {val ? 'Replace video' : 'Upload video'}</span>
                                  <input id={'inline-vid-'+f.id} type="file" accept="video/*" style={{display:'none'}}
                                    onChange={async e => {
                                      const file = e.target.files?.[0];
                                      if (!file) return;
                                      const fd = new FormData(); fd.append('image', file);
                                      try {
                                        const res = await fetch(process.env.REACT_APP_API_URL+'api/upload', {method:'POST',body:fd});
                                        const data = await res.json();
                                        if (data.url) handleLocalChange(f.name, data.url);
                                      } catch(err) { alert('Upload failed'); }
                                    }} />
                                </div>
                                {val && <video src={val} controls style={{width:200,height:120,objectFit:'cover',borderRadius:6,marginTop:8,display:'block'}} />}
                              </div>
                            ) : isImage ? (
                              <div>
                                <div style={{display:'flex',alignItems:'center',gap:8,padding:'7px 10px',border:'1px dashed var(--border2)',borderRadius:'var(--r-sm)',background:'var(--bg)',cursor:'pointer'}}
                                  onClick={() => document.getElementById('inline-img-'+f.id)?.click()}>
                                  <span style={{fontSize:12,color:'var(--muted2)'}}>⬆ {val ? 'Replace image' : 'Upload image'}</span>
                                  <input id={'inline-img-'+f.id} type="file" accept="image/*" style={{display:'none'}}
                                    onChange={async e => {
                                      const file = e.target.files?.[0];
                                      if (!file) return;
                                      const fd = new FormData(); fd.append('image', file);
                                      try {
                                        const res = await fetch(process.env.REACT_APP_API_URL+'api/upload', {method:'POST',body:fd});
                                        const data = await res.json();
                                        if (data.url) { handleLocalChange(f.name, data.url); }
                                      } catch(err) { alert('Upload failed'); }
                                    }} />
                                </div>
                                {val && <img src={val} alt="" style={{width:200,height:130,objectFit:'cover',borderRadius:6,marginTop:8,display:'block'}} onError={e=>e.target.style.display='none'} />}
                                {val && <div style={{fontSize:10,color:'var(--muted)',marginTop:3,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>✓ {val.includes('/ipfs/') ? 'Uploaded to Pinata' : 'Image set'}</div>}
                                {val && (
                                  <details className="image-seo-details" style={{marginTop:8}}>
                                    <summary style={{fontSize:11,color:'var(--muted2)',cursor:'pointer',userSelect:'none'}}>Alt text, caption, description, credit ▾</summary>
                                    <div style={{marginTop:8,display:'flex',flexDirection:'column',gap:8}}>
                                    <div>
                                      <label style={{fontSize:10,color:'var(--muted)',display:'block',marginBottom:3}}>Alt Text (for SEO / accessibility)</label>
                                      <input className="minput inline-input" style={{fontSize:12}}
                                        value={localData[f.name + '_alt'] || ''}
                                        onChange={e => handleLocalChange(f.name + '_alt', e.target.value)}
                                        placeholder="Describe this image..." />
                                    </div>
                                    <div>
                                      <label style={{fontSize:10,color:'var(--muted)',display:'block',marginBottom:3}}>Caption</label>
                                      <input className="minput inline-input" style={{fontSize:12}}
                                        value={localData[f.name + '_caption'] || ''}
                                        onChange={e => handleLocalChange(f.name + '_caption', e.target.value)}
                                        placeholder="Text shown under the image..." />
                                    </div>
                                    <div>
                                      <label style={{fontSize:10,color:'var(--muted)',display:'block',marginBottom:3}}>Description</label>
                                      <textarea className="minput inline-input" rows={2} style={{fontSize:12}}
                                        value={localData[f.name + '_description'] || ''}
                                        onChange={e => handleLocalChange(f.name + '_description', e.target.value)}
                                        placeholder="Longer internal/SEO description of the image..." />
                                    </div>
                                    <div>
                                      <label style={{fontSize:10,color:'var(--muted)',display:'block',marginBottom:3}}>Credit / Attribution</label>
                                      <input className="minput inline-input" style={{fontSize:12}}
                                        value={localData[f.name + '_credit'] || ''}
                                        onChange={e => handleLocalChange(f.name + '_credit', e.target.value)}
                                        placeholder="Photographer or source credit..." />
                                    </div>
                                    </div>
                                  </details>
                                )}
                              </div>
                            ) : f.type === 'file' ? (
                              <div>
                                <div style={{display:'flex',alignItems:'center',gap:8,padding:'7px 10px',border:'1px dashed var(--border2)',borderRadius:'var(--r-sm)',background:'var(--bg)',cursor:'pointer'}}
                                  onClick={() => document.getElementById('inline-file-'+f.id)?.click()}>
                                  <span style={{fontSize:12,color:'var(--muted2)'}}>📎 {val ? 'Replace file' : 'Upload file'}</span>
                                  <input id={'inline-file-'+f.id} type="file" style={{display:'none'}}
                                    onChange={async e => {
                                      const file = e.target.files?.[0];
                                      if (!file) return;
                                      const fd = new FormData(); fd.append('image', file);
                                      try {
                                        const res = await fetch(process.env.REACT_APP_API_URL+'api/upload', {method:'POST',body:fd});
                                        const data = await res.json();
                                        if (data.url) { handleLocalChange(f.name, data.url); }
                                      } catch(err) { alert('Upload failed'); }
                                    }} />
                                </div>
                                {val && <div style={{fontSize:11,color:'var(--muted)',marginTop:8,padding:'8px 10px',background:'var(--bg)',borderRadius:4,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',border:'1px solid var(--border2)'}}>✓ {val.split('/').pop()}</div>}
                              </div>
                            ) : isBool ? (
                              <select className="minput inline-input" value={val === true || val === 'true' ? 'true' : 'false'} onChange={e => handleLocalChange(f.name, e.target.value === 'true')}>
                                <option value="true">True</option>
                                <option value="false">False</option>
                              </select>
                            ) : isHtml ? (
                                <div className="ql-wrapper">
                                  <HtmlEditor
                                    value={val || ''}
                                    onChange={value => handleLocalChange(f.name, value)}
                                  />
                                </div>
                            ) : isText ? (
                              <textarea className="minput inline-input" rows={3} value={val || ''} onChange={e => handleLocalChange(f.name, e.target.value)} placeholder={`Enter ${f.label}...`} />
                            ) : (
                              <input className="minput inline-input" type={f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : 'text'}
                                value={val || ''} onChange={e => handleLocalChange(f.name, f.type === 'number' ? parseFloat(e.target.value)||0 : e.target.value)}
                                placeholder={`Enter ${f.label}...`} />
                            )}
                          </div>
                        )}

                        {!isArray && !canEdit && (
                          <div className="inline-field-readonly">
                            {isHtml
                              ? <div dangerouslySetInnerHTML={{__html: val || ''}} style={{fontSize:13,color:'var(--muted2)'}} />
                              : val ? String(val) : <span style={{color:'var(--muted)'}}>No data</span>
                            }
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}

        {tab === 'api' && (
          <div className="editor-body">
            {!activePage ? <div className="editor-empty"><div>Select a page to see the API</div></div> : (
              <>
                <div className="api-block">
                  <div className="api-label">Endpoints</div>
                  <pre className="api-code">{`GET /api/query?q=*[_type == "${activePage.slug}"]
GET /api/query?q=*[_type == "${activePage.slug}"][0]
GET /api/query?q=*[_id == "DOCUMENT_ID"][0]`}</pre>
                </div>
                <div className="api-block">
                  <div className="api-label">Use in React / Next.js</div>
                  <pre className="api-code">{`// All sections for this page
const res = await fetch('http://localhost:5000/api/query?q=*[_type == "${activePage.slug}"]');
const { result } = await res.json();

// First section only
const res2 = await fetch('http://localhost:5000/api/query?q=*[_type == "${activePage.slug}"][0]');
const { result: section } = await res2.json();`}</pre>
                </div>
                <div className="api-block">
                  <div className="api-label">Response</div>
                  <pre className="api-code">{JSON.stringify(topSections.filter(s => s.published).map(s => ({ _id: s._id, title: s.title, ...s.tree })), null, 2) || '// No published sections yet'}</pre>
                </div>
              </>
            )}
          </div>
        )}
      </main>
      </>
      )}

      {modal === 'addPage' && canAddOrDelete && <NameModal title="New Page" placeholder="e.g. Home, About, Blog..." onConfirm={handleAddPage} onClose={() => setModal(null)} />}
      {modal === 'addTopSection' && canAddOrDelete && <NameModal title="New Section" placeholder="e.g. Hero, Footer, Features..." onConfirm={handleAddTopSection} onClose={() => setModal(null)} showKey={true} />}
      {modal === 'addBlogPost' && canAddOrDelete && <NameModal title="New Blog Post" placeholder="e.g. Why Solar Makes Sense in 2026" onConfirm={handleNewBlogPost} onClose={() => setModal(null)} />}
      {modal === 'addSection' && canAddOrDelete && <NameModal title="New Sub-section" placeholder="e.g. Card 1, Link Group..." onConfirm={handleAddSubSection} onClose={() => setModal(null)} showKey={true} />}
      {modal === 'addField' && canAddOrDelete && <AddFieldModal onConfirm={handleAddField} onClose={() => setModal(null)} />}
      {editingSchema && canAddOrDelete && (
        <EditSubFieldsModal
          field={editingSchema}
          onConfirm={handleUpdateSubFields}
          onClose={() => setEditingSchema(null)}
        />
      )}
      {editingSingleItem && rightPane && canEdit && (
        <EditSingleItemModal
          field={editingSingleItem.field}
          item={editingSingleItem.item}
          onSave={updatedItem => {
            const arrName = editingSingleItem.field.name;
            const arr = [...((rightPane.data || {})[arrName] || [])];
            arr[editingSingleItem.index] = { ...arr[editingSingleItem.index], ...updatedItem };
            handleLocalChange(arrName, arr);
            setEditingSingleItem(null);
          }}
          onClose={() => setEditingSingleItem(null)}
        />
      )}
      {showAI && activePage && canEdit && (
        <AIAssist
          onApply={async ({ sectionName, fields }) => {
            if (!activePage) return;
            const builtFields = fields
              .filter(f => f.type !== 'array')
              .map(f => ({ id: uid(), name: f.name, label: f.name, type: f.type }));
            const builtArrayFields = fields
              .filter(f => f.type === 'array')
              .map(f => ({
                id: uid(), name: f.name, label: f.name, type: 'array',
                subFields: (f.subFields || []).map(sf => ({ id: uid(), name: sf, label: sf, type: 'string' }))
              }));
            const allFields = [...builtFields, ...builtArrayFields];
            const data = {};
            fields.forEach(f => {
              if (f.type === 'array') data[f.name] = [];
              else if (f.type === 'boolean') data[f.name] = false;
              else if (f.type === 'number') data[f.name] = 0;
              else data[f.name] = f.value || '';
            });
            const existingKeys = topSections.map(s => s.tree?._key || '');
            if (existingKeys.includes(sectionName)) {
              alert('Section key already exists. Please rename it in the review step.');
              return;
            }
            const newTree = { id: uid(), _key: sectionName, name: sectionName, label: sectionName, children: [], fields: allFields, data };
            try {
              const res = await API.post('/api/documents', {
                schemaId: activePage._id, schemaSlug: activePage.slug,
                title: sectionName, fields: allFields, data: { tree: newTree }, published: false
              });
              const doc = { _id: res.data._id, title: sectionName, published: false, tree: newTree };
              setTopSections(p => [...p, doc]);
            } catch(e) { alert('Error creating section'); }
            setShowAI(false);
          }}
          onClose={() => setShowAI(false)}
        />
      )}
      {editingField && rightPane && canAddOrDelete && (
        <EditFieldModal
          field={editingField}
          value={(rightPane.data || {})[editingField.name]}
          onSave={val => { handleLocalChange(editingField.name, val); setEditingField(null); }}
          onAutoSave={val => handleFieldSave(editingField.name, val)}
          onClose={() => setEditingField(null)}
        />
      )}
      {editingFieldLabel && canEdit && (
        <EditFieldLabelModal
          field={editingFieldLabel}
          onConfirm={handleUpdateFieldLabel}
          onClose={() => setEditingFieldLabel(null)}
        />
      )}
      {editingSectionLabel && canEdit && (
        <Modal onClose={() => setEditingSectionLabel(null)}>
          <div className="modal-title">Edit Section Label</div>
          <label className="mlabel">Display Name</label>
          <input className="minput"
            defaultValue={editingSectionLabel.label || ''}
            placeholder={editingSectionLabel.isTopSection ? editingSectionLabel.title : editingSectionLabel.name}
            onKeyDown={e => e.key === 'Enter' && handleUpdateSectionLabel(e.target.value)}
            autoFocus />
          <div style={{fontSize:11,color:'var(--muted)',marginTop:8}}>
            Section "{editingSectionLabel.isTopSection ? editingSectionLabel.title : editingSectionLabel.name}" will remain unchanged
          </div>
          <div className="mactions">
            <button className="btn-ghost" onClick={() => setEditingSectionLabel(null)}>Cancel</button>
            <button className="btn-solid" onClick={(e) => {
              const input = e.target.closest('.modal').querySelector('input');
              handleUpdateSectionLabel(input.value);
            }}>Update</button>
          </div>
        </Modal>
      )}
    </div>
  );
}